import { scoreAndFilterByEmbedding, usePrecomputedEmbeddings } from '@shared/utils/search';
import type { FuseOptions, FuseResultWithScore } from 'fuse.js';
import type Fuse from 'fuse.js';
import { useMemo } from 'react';
import type { StationeryTemplate } from '../../DesignsGallery.types';
import { useValuePropsPayload } from '../../useValuePropsPayload';
import type { GalleryTemplate, GalleryValueProp } from '../GalleryItem/GalleryItem';
import { EMBEDDINGS_STORE, SIMILARITY_THRESHOLD } from './Search.constants';
import { useSearchContext } from './SearchContext';

function getThemeIdOrEquivalent(item: GalleryTemplate | GalleryValueProp) {
  if (item.type === 'prop') return `valueprop_${item.telemetryId ?? ''}`;
  return item.themeId;
}

export function useSearchFilteredGalleryItems(templates: ReadonlyArray<GalleryTemplate>, fuse: Fuse<StationeryTemplate, FuseOptions<StationeryTemplate>>) {
  const { themes, embeddingMap } = usePrecomputedEmbeddings(EMBEDDINGS_STORE);
  const { activeTerm: term, activeTermEmbedding: termEmbedding } = useSearchContext();
  const isActive = !!termEmbedding;
  const valuePropsData = useValuePropsPayload();

  const filteredItems = useMemo(() => {
    if (!themes) return templates; // Means still loading
    if (!isActive) return templates;

    const valueProps: ReadonlyArray<GalleryValueProp> = (valuePropsData?.props ?? []).map(prop => ({
      ...prop,
      type: 'prop',
    }));
    const items = [...templates, ...valueProps];

    const fuseResults = fuse.search(term) as FuseResultWithScore<StationeryTemplate>[];

    return scoreAndFilterByEmbedding(items, {
      termEmbedding,
      themes,
      embeddingMap,
      getThemeId: getThemeIdOrEquivalent,
      fuseResults,
      getFuseItemThemeId: item => item.themeId,
      threshold: SIMILARITY_THRESHOLD,
    });
  }, [templates, valuePropsData, fuse, themes, embeddingMap, term, termEmbedding, isActive]);

  return {
    isActive,
    filteredItems,
  };
}
